import { Component, Input, OnInit, signal } from '@angular/core';
import { SelectItem } from 'primeng/api';
import { PeriforOnChangeMessages, SignalRService } from '../../../shared/services/signal-r.service';
import { StressResultsService } from '../../services/stress-results.service';
import { toCamelCaseString } from 'src/app/shared/helpers/stringToCamel.helper';
import { forkJoin, lastValueFrom, Observable } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { SessionStorageKeys, StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { SingleLoadResultsUi } from '../../models/perivis-results-state.model';
import { Store } from '@ngneat/elf';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { CurrentAppIdsDto } from 'src/app/core/models/current-app-ids-dto';
import { PostYieldService } from '../../services/post-yield.service';

@Component({
  selector: 'app-single-load-holliday-stress-ratio',
  templateUrl: './single-load-holliday-stress-ratio.html',
  styles: [],
  standalone: false
})
export class SingleLoadHollidayStressRatioComponent implements OnInit {
  private readonly hollidayDfField = 'hollidayStressRatioDf';
  private selectedString = '';

  public hollidayStressRatioResults: any[] | null = null;
  public loadCases: SelectItem[] = [];
  public resultsDisplay: SelectItem[] = [
    { label: 'Plot', value: 'plot' },
    { label: 'Grid', value: 'grid' }
  ];
  public modes: SelectItem[] = [
    { label: 'Single', value: 'single' },
    { label: 'Multiple', value: 'multiple' }
  ];
  public errorMsg: string | null = null;
  public results: any[] = [];
  public depthView: SelectItem[] = [
    { label: 'MD', value: 'md' },
    { label: 'TVD', value: 'tvd' }
  ];
  public isLoading = signal<boolean>(true);
  public selectedMode: string = 'single';
  public selectedResultDisplay: string = 'plot';
  public selectedDepthView: string = 'md';
  public yAxisTitle = '';
  public xAxisTitle = 'Holliday Stress Ratio';
  public userUnits: UserUnitsModel | null = null;
  public plotName = '';
  public signalRChange = false;
  public columnDefs: { header: string, field: string }[] = [];
  public columnDefinitions: any[] = [];
  public longLengthUnit = '';
  public hollidayStressRatio: number | null = null;

  // State management
  private _componentId = '';
  @Input() set componentId(value: string) {
    this._componentId = value;
    this.hollidayStressRatioStore = this._storeService.createStore(this.componentId, new SingleLoadResultsUi(), withUIEntities<XyLinePlotUi>());
    this.selectedMode = this.hollidayStressRatioStore.state.selectedMode || 'single';
    this.selectedResultDisplay = this.hollidayStressRatioStore.state.selectedResultDisplay || 'plot';
    this.selectedDepthView = this.hollidayStressRatioStore.state.selectedDepthView || 'md';
  }
  get componentId(): string {
    return this._componentId;
  }
  public hollidayStressRatioStore!: Store;

  constructor(
    private _stressResultsService: StressResultsService,
    private _signalRService: SignalRService,
    private _storeService: StoreService,
    private _postYieldService: PostYieldService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);
    this.longLengthUnit = this.userUnits?.longLengths || '';
    this.yAxisTitle = `MD (${this.longLengthUnit})`;

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
    this.selectedString = appIds?.tubularId || '';
    this.refreshResults(true, false, false);

    this.columnDefs = [
      { header: 'Holliday Stress Ratio', field: 'hollidayStressRatio' }
    ];
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: 'Holliday Stress Ratio', field: 'hollidayStressRatio',
        valueFormatter: (params: any) => this.formatDecimal(params, 1000)
      }
    ];
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS ||
      data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS ||
      data.action == PeriforOnChangeMessages.REFRESH_HOLLIDAY_STRESS) {
      const appIds = this._storeService.getSession<CurrentAppIdsDto>(SessionStorageKeys.APP_IDS);
      this.selectedString = appIds?.tubularId || '';
      this.refreshResults(true, true, false);
    }
  }

  public async refreshResults(isLoading: boolean, calledFromSignalR: boolean, calledFromMode: boolean): Promise<void> {
    this.isLoading.set(isLoading);

    this.xAxisTitle = 'Holliday Stress Ratio';

    if ((!this.hollidayStressRatioResults || calledFromSignalR) && !calledFromMode) {
      try {
        const sources = [
          this._stressResultsService.getHollidayStressRatioResults() as Observable<any>,
          this._postYieldService.getPostYield() as Observable<any>
        ];
        const [hollidayStressRatioResults, postYield] = await lastValueFrom(forkJoin(sources));
        this.hollidayStressRatioResults = hollidayStressRatioResults;
        this.hollidayStressRatio = postYield?.postYieldAnalsisParameters?.hollidayStressRatio ?? 1;
        this.signalRChange = true;
      } catch {
        this.errorMsg = 'Well Configuration Missing: Please specify at least one string';
        this.isLoading.set(false);
        return;
      }
    }

    if (this.hollidayStressRatioResults && (this.hollidayStressRatioResults.length === 0 || this.hollidayStressRatioResults[0].loadCaseName === '' || this.hollidayStressRatioResults[0].hollidayStressRatioResults === null)) {
      this.errorMsg = 'Please calculate PERIVIS to see results';
      this.columnDefs = [];
      this.results = [];
      this.isLoading.set(false);
      return;
    } else {
      this.errorMsg = null;
    }

    if (!this.hollidayStressRatioResults) {
      this.isLoading.set(false);
      return;
    }

    if (this.hollidayStressRatioStore.state.selectedMode == 'single') {
      this.handleSingleHollidayStressRatio(this.hollidayStressRatioResults);
    } else {
      this.handleMultiHollidayStressRatio(this.hollidayStressRatioResults);
    }

    this.isLoading.set(false);
  }

  onModeChange(e: any): void {
    this.selectedMode = e.value;
    this.hollidayStressRatioStore.update(state => ({ ...state, selectedMode: e.value }));
    if (this.hollidayStressRatioStore.state.selectedMode == 'multiple') {
      this.hollidayStressRatioStore.update(state => ({ ...state, selectedLoadCase: { label: 'Holliday Stress Ratio', value: 'hollidayStressRatio' } }));
    }

    this.plotName = this.setPlotName();
    this.refreshResults(true, false, true);
  }

  onSelectedResultDisplayUpdated(e: any): void {
    this.selectedResultDisplay = e.value;
    this.hollidayStressRatioStore.update(state => ({ ...state, selectedResultDisplay: e.value }));
  }

  setLoadCase(e: any): void {
    if (this.hollidayStressRatioStore.state.selectedMode === 'single') {
      this.hollidayStressRatioStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.results = this.withHollidayDesignFactor(e.value.value.hollidayStressRatioResults || []);
    } else {
      this.hollidayStressRatioStore.update(state => ({ ...state, selectedLoadCase: e.value }));
      this.plotName = this.setPlotName();
      this.refreshResults(true, false, false);
    }
  }

  setPlotName(): string {
    const selectedMode = this.hollidayStressRatioStore?.state?.selectedMode || this.selectedMode;
    const selectedLoadCaseLabel = this.hollidayStressRatioStore?.state?.selectedLoadCase?.label || '';
    return 'hollidayStressRatioPlot - ' + this.selectedString + ' - ' + selectedMode + ' ' + selectedLoadCaseLabel;
  }

  public depthViewToggle(e: any) {
    this.selectedDepthView = e.value;
    this.hollidayStressRatioStore.update(state => ({ ...state, selectedDepthView: e.value }));
  }

  private formatDecimal(value: any, divisor: number): string | null {
    return value || value === 0 ? (Math.trunc(value * divisor) / divisor).toLocaleString('en-US') : null;
  }

  private handleSingleHollidayStressRatio(hollidayStressRatioResults: any[]) {
    this.columnDefs = [
      { header: 'Holliday Stress Ratio', field: 'hollidayStressRatio' },
      { header: 'Critical Holliday Stress Ratio', field: this.hollidayDfField }
    ];
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: 'Holliday Stress Ratio', field: 'hollidayStressRatio',
        valueFormatter: (params: any) => this.formatDecimal(params, 1000)
      }
    ];

    this.results = [];

    this.loadCases = hollidayStressRatioResults?.map(lc => {
      return { label: lc.loadCaseName, value: lc };
    }) ?? [];
    if (!this.hollidayStressRatioStore.state.selectedLoadCase || !this.loadCases.find(l => l.value.tubularStringId == this.hollidayStressRatioStore.state.selectedLoadCase?.value.tubularStringId)) {
      this.hollidayStressRatioStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
    }
    this.plotName = this.setPlotName();
    this.results = this.withHollidayDesignFactor(this.hollidayStressRatioStore.state.selectedLoadCase?.value.hollidayStressRatioResults || []);
  }

  private handleMultiHollidayStressRatio(hollidayStressRatioResults: any[]) {
    this.columnDefinitions = [
      {
        header: `MD (${this.longLengthUnit})`, field: 'measuredDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      },
      {
        header: `TVD (${this.longLengthUnit})`, field: 'trueVerticalDepth',
        valueFormatter: (params: any) => this.formatDecimal(params, 100)
      }
    ];
    this.columnDefs = [
      { header: 'Holliday Stress Ratio', field: this.hollidayDfField }
    ];
    this.results = [];

    this.loadCases = [{ label: 'Holliday Stress Ratio', value: 'hollidayStressRatio' }];
    if (!this.hollidayStressRatioStore.state.selectedLoadCase || this.hollidayStressRatioStore.state.selectedLoadCase.value !== 'hollidayStressRatio') {
      this.hollidayStressRatioStore.update(state => ({ ...state, selectedLoadCase: this.loadCases[0] }));
    }

    hollidayStressRatioResults.forEach(load => {
      const loadNameCamel = toCamelCaseString(load.loadCaseName);

      this.columnDefs.push({ header: load.loadCaseName, field: loadNameCamel });

      this.columnDefinitions.push({
        header: load.loadCaseName,
        field: loadNameCamel,
        valueFormatter: (params: any) => this.formatDecimal(params, 1000)
      });

      const backupArray: any[] = [];

      load.hollidayStressRatioResults.forEach((loadResult: any) => {
        const result = {
          [loadNameCamel]: loadResult.hollidayStressRatio,
          measuredDepth: loadResult.measuredDepth,
          trueVerticalDepth: loadResult.trueVerticalDepth
        };

        if (hollidayStressRatioResults[0].loadCaseName === load.loadCaseName) {
          this.results.push(result);
        } else {
          backupArray.push(result);
        }
      });

      this.results = this.results.map(x => {
        const otherLoad = backupArray.find(e => e.measuredDepth === x.measuredDepth);
        return { ...x, ...otherLoad };
      });
    });

    this.results = this.withHollidayDesignFactor(this.results);

    this.plotName = this.setPlotName();
  }

  private withHollidayDesignFactor(results: any[]): any[] {
    const ratio = this.hollidayStressRatio ?? 1;
    return results.map(result => ({
      ...result,
      [this.hollidayDfField]: ratio
    }));
  }
}
